import {
  ACTION_FAMILY,
  type ActionType,
  type ExplicitActionType,
} from "./action-types";

export const ACTION_CATEGORIES = {
  // Every VM's transfer executor. A chain registers only its own VM's, so the
  // family resolves by chain first, then by the asset kinds each moves
  // (`transferExecutorMovesAsset`).
  [ACTION_FAMILY.transfer]: [
    "erc20_transfer",
    "native_transfer",
    "spl_transfer",
  ],
  [ACTION_FAMILY.yieldWithdraw]: [
    "erc4626_withdraw",
    "fluid_vault_withdraw",
    "morpho_vault_withdraw",
    "aave_v3_withdraw",
    "aave_v3_gateway_withdraw",
    "weth_unwrap",
  ],
  [ACTION_FAMILY.yieldRedeem]: [
    "erc4626_redeem",
    "fluid_vault_redeem",
    "morpho_vault_redeem",
    "aave_v3_withdraw",
  ],
  [ACTION_FAMILY.swap]: [
    "lifi_swap",
    "relay_swap",
    "bebop_rfq_swap",
    "bebop_jam_swap",
    "ondo_direct_mint_redeem",
    "ondo_direct_batch_rebalance",
    "reality_via_bitget_swap",
    "zeroex_swap",
  ],
  [ACTION_FAMILY.bridge]: ["lifi_bridge", "relay_bridge"],
  [ACTION_FAMILY.yieldDeposit]: [
    "erc4626_deposit",
    "fluid_vault_deposit",
    "morpho_vault_deposit",
    "aave_v3_deposit",
    "aave_v3_gateway_deposit",
    "weth_wrap",
  ],
  [ACTION_FAMILY.liquidity]: [
    "cl_mint",
    "cl_increase",
    "cl_decrease",
    "cl_collect",
    "cl_burn",
  ],
  [ACTION_FAMILY.call]: ["evm_call"],
} as const satisfies Record<ActionType, readonly ExplicitActionType[]>;

/**
 * The CAIP-19 asset namespaces each transfer executor moves. A new VM's
 * transfer executor is one entry here and one in the transfer family.
 */
const TRANSFER_ASSET_NAMESPACES: ReadonlyMap<
  ExplicitActionType,
  readonly string[]
> = new Map<ExplicitActionType, readonly string[]>([
  ["erc20_transfer", ["erc20"]],
  ["native_transfer", ["slip44"]],
  // The Solana vault transfer: SPL tokens and native SOL.
  ["spl_transfer", ["spl", "slip44"]],
]);

export const transferExecutorMovesAsset = (
  actionType: ExplicitActionType,
  assetNamespace: string,
): boolean =>
  TRANSFER_ASSET_NAMESPACES.get(actionType)?.includes(assetNamespace) ?? false;

/**
 * The transfer executors that pay a fee leg (`args.fee`) out of the
 * transferred amount in the same transaction. A transfer that names a fee
 * resolves only to one of these, so no executor silently drops the leg.
 */
const TRANSFER_FEE_LEG_EXECUTORS: ReadonlySet<ExplicitActionType> =
  new Set<ExplicitActionType>([
    "erc20_transfer",
    "native_transfer",
    "spl_transfer",
  ]);

export const transferExecutorPaysFeeLeg = (
  actionType: ExplicitActionType,
): boolean => TRANSFER_FEE_LEG_EXECUTORS.has(actionType);

export const getCategoryCandidates = (
  actionType: ActionType,
): readonly ExplicitActionType[] => ACTION_CATEGORIES[actionType];
